import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import {
  useLazyInitializeCancellationFlowQuery,
  useSubmitCancelFeedbackMutation,
  useSubmitCancelOptionMutation,
  useSubmitZipCodeReleaseMutation
} from "../campaigns-api-endpoints";
import AccountSummaryStep from "./cancel-steps/account-summary-step";
import CancelFeedbackStep from "./cancel-steps/cancel-feedback-step";
import CancelOptionsStep from "./cancel-steps/cancel-options-step";
import ReleaseZipCodesStep from "./cancel-steps/release-zip-codes-step";
import ConfirmationStep from "./cancel-steps/confirmation-step";
import ReviewResultsStep from "./cancel-steps/review-results-step";
import RetargetingStatsStep from "./cancel-steps/retargeting-stats-step";
import FbReviewStep from "./cancel-steps/fb-review-step";
import PriceIncreasedStep from "./cancel-steps/price-increased-step";
import CouponSavingsStep from "./cancel-steps/coupon-savings-step";
import SoldOutZipsStep from "./cancel-steps/soldout-zips-step";
import RetentionCouponStep from "./cancel-steps/retention-coupon-step";

const STEPS = {
  ACCOUNT_SUMMARY: "account_summary",
  CANCEL_FEEDBACK: "cancel_feedback",
  REVIEW_RESULTS: "review_results",
  RETARGETING_STATS: "retargeting_stats",
  FB_REVIEW: "fb_review",
  PRICE_INCREASED: "price_increased",
  COUPON_SAVINGS: "coupon_savings",
  SOLDOUT_ZIPS: "soldout_zips",
  RETENTION_COUPON: "retention_coupon",
  CANCEL_OPTIONS: "cancel_options",
  RELEASE_ZIP_CODES: "release_zip_codes",
  CONFIRMATION: "confirmation"
};

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cancelLink: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired
};

const CancelFlowModal = ({ isOpen, onClose, cancelLink, campaignId, campaignType }) => {
  const [currentStep, setCurrentStep] = useState(null);
  const [releaseFormType, setReleaseFormType] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [stepError, setStepError] = useState(null);
  const [releaseErrors, setReleaseErrors] = useState(null);

  const [initFlow, { data: flowData, isLoading: isInitializing, error: initError }] =
    useLazyInitializeCancellationFlowQuery();
  const [submitFeedback, { isLoading: isFeedbackLoading }] = useSubmitCancelFeedbackMutation();
  const [submitOption, { isLoading: isOptionLoading }] = useSubmitCancelOptionMutation();
  const [submitRelease, { isLoading: isReleaseLoading }] = useSubmitZipCodeReleaseMutation();

  useEffect(() => {
    if (!isOpen) return;
    initFlow(cancelLink);
    setCurrentStep(null);
    setStepError(null);
    setReleaseErrors(null);
    setConfirmationResult(null);
  }, [isOpen, initFlow]);

  useEffect(() => {
    if (!flowData || currentStep) return;
    setCurrentStep(flowData.steps[0] || STEPS.CANCEL_OPTIONS);
    mixpanelService.track("Campaign.Cancel.Start", {
      ...pageData.get("mixpanelData"),
      campaignId,
      campaignType
    });
  }, [flowData, currentStep]);

  const advanceStep = () => {
    const idx = flowData.steps.indexOf(currentStep);
    if (idx >= 0 && idx < flowData.steps.length - 1) {
      setCurrentStep(flowData.steps[idx + 1]);
    }
  };

  const handleKeep = () => onClose();

  const handleFeedbackSubmit = async ({ feedbackOption, otherText }) => {
    setStepError(null);
    try {
      const result = await submitFeedback({
        url: flowData.urls.cancel_feedback,
        feedbackOption,
        otherText
      }).unwrap();

      if (result.action === "cancelled") {
        setConfirmationResult(result);
        setCurrentStep(STEPS.CONFIRMATION);
      } else {
        advanceStep();
      }
    } catch (_e) {
      setStepError("Unable to save your feedback. Please try again.");
    }
  };

  const handleOptionSubmit = async cancelOption => {
    setStepError(null);
    try {
      const result = await submitOption({
        url: flowData.urls.cancel || flowData.urls.cancel_options,
        cancelOption
      }).unwrap();

      if (result.action === "error") {
        setStepError(result.errors?.[0] || "Something went wrong. Please try again.");
      } else if (result.action === "requires_release_form") {
        setReleaseFormType(result.form_type);
        setCurrentStep(STEPS.RELEASE_ZIP_CODES);
      } else {
        setConfirmationResult(result);
        setCurrentStep(STEPS.CONFIRMATION);
      }
    } catch (_e) {
      setStepError("Something went wrong. Please try again.");
    }
  };

  const handleReleaseSubmit = async ({ fullName, agentRelationship }) => {
    setReleaseErrors(null);
    const url =
      releaseFormType === "disable_renewal"
        ? flowData.urls.disable_renewal_save
        : flowData.urls.release_zip_codes_save;

    try {
      const result = await submitRelease({ url, fullName, agentRelationship }).unwrap();
      setConfirmationResult(result);
      setCurrentStep(STEPS.CONFIRMATION);
    } catch (err) {
      setReleaseErrors(err?.data?.errors || ["Something went wrong. Please try again."]);
    }
  };

  const renderStep = () => {
    if (initError) {
      return (
        <div className="notifications danger mt-3">
          <div className="notifications__error-message">
            Unable to load cancellation options. Please try again.
          </div>
        </div>
      );
    }

    if (isInitializing || !flowData) {
      return (
        <div className="text-center py-4">
          <Spinner color="primary" />
        </div>
      );
    }

    const cfd = flowData.cancel_flow_data;

    switch (currentStep) {
      case STEPS.ACCOUNT_SUMMARY:
        return (
          <AccountSummaryStep flowData={flowData} onContinue={advanceStep} onKeep={handleKeep} />
        );
      case STEPS.REVIEW_RESULTS:
        return (
          <ReviewResultsStep
            reviewResults={flowData.review_results}
            onContinue={advanceStep}
            onKeep={handleKeep}
          />
        );
      case STEPS.RETARGETING_STATS:
        return (
          <RetargetingStatsStep cancelFlowData={cfd} onContinue={advanceStep} onKeep={handleKeep} />
        );
      case STEPS.FB_REVIEW:
        return (
          <FbReviewStep
            likesCount={flowData.fb_likes_count}
            commentsCount={flowData.fb_comments_count}
            onContinue={advanceStep}
            onKeep={handleKeep}
          />
        );
      case STEPS.PRICE_INCREASED:
        return (
          <PriceIncreasedStep cancelFlowData={cfd} onContinue={advanceStep} onKeep={handleKeep} />
        );
      case STEPS.COUPON_SAVINGS:
        return (
          <CouponSavingsStep cancelFlowData={cfd} onContinue={advanceStep} onKeep={handleKeep} />
        );
      case STEPS.SOLDOUT_ZIPS:
        return (
          <SoldOutZipsStep cancelFlowData={cfd} onContinue={advanceStep} onKeep={handleKeep} />
        );
      case STEPS.RETENTION_COUPON:
        return (
          <RetentionCouponStep
            cancelFlowData={cfd}
            yearlySavings={flowData.yearly_savings}
            isLoading={isOptionLoading}
            error={stepError}
            onClaim={() => handleOptionSubmit("retention_coupon")}
            onSkip={advanceStep}
          />
        );
      case STEPS.CANCEL_FEEDBACK:
        return (
          <CancelFeedbackStep
            feedbackOptions={flowData.feedback_options}
            isLoading={isFeedbackLoading}
            error={stepError}
            onSubmit={handleFeedbackSubmit}
            onKeep={handleKeep}
          />
        );
      case STEPS.CANCEL_OPTIONS:
        return (
          <CancelOptionsStep
            cancelOptions={flowData.cancel_options}
            isLoading={isOptionLoading}
            error={stepError}
            onSubmit={handleOptionSubmit}
            onKeep={handleKeep}
          />
        );
      case STEPS.RELEASE_ZIP_CODES:
        return (
          <ReleaseZipCodesStep
            isDisableRenewal={releaseFormType === "disable_renewal"}
            zipSpots={flowData.zip_spots}
            isLoading={isReleaseLoading}
            error={releaseErrors}
            onSubmit={handleReleaseSubmit}
            onKeep={handleKeep}
          />
        );
      case STEPS.CONFIRMATION:
        return <ConfirmationStep result={confirmationResult} onClose={onClose} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleKeep}
      fade={false}
      className="account-app modal-dialog-centered"
      size="lg"
      data-testid="cancel-flow-modal"
    >
      <ModalHeader toggle={handleKeep} className="p-2" />
      <ModalBody className="px-4 py-0">
        <h5 className="font-weight-bold text-center mb-3">Cancel Campaign</h5>
        {renderStep()}
      </ModalBody>
    </Modal>
  );
};

CancelFlowModal.propTypes = propTypes;

export default CancelFlowModal;
